import { AiContextModalFrame } from '@/components/ai-context-modal-frame';

import { useEffect, useState } from 'react';
import { useInstructionCreate } from '@/hooks/rxdb-models';

import { type GroupedInstruction, InstructionsPanel } from '@/components/instructions-panel';

import styles from './manage-instructions-modal.module.scss';

export const ManageInstructionsModal = (props: ManageInstructionsModalProps) => {
  const {
    open,
    empty,
    onClose,
    onInstructionCreate,
    onInstructionDelete,
    onInstructionEnabledChange,
    documentInstructions,
    groupedInstructions,
    editorTestId,
  } = props;

  const [instructionToFocus, setInstructionToFocus] = useState<string>();
  const [groupToExpand, setGroupToExpand] = useState<string>();

  const { mutate: createInstruction } = useInstructionCreate();

  const handleCreateInstruction = async () => {
    const instruction = await createInstruction({
      name: 'Instruction',
    });
    if (!instruction) return;

    const instructionId = instruction.get('id');
    await onInstructionCreate?.(instructionId);

    const targetGroup = groupedInstructions?.find((g) => g.type !== 'blocks');
    if (targetGroup) {
      setGroupToExpand(undefined);
      setTimeout(() => setGroupToExpand(targetGroup.id), 0);
    }

    setInstructionToFocus(instructionId);
  };

  useEffect(() => {
    if (open) {
      setInstructionToFocus(undefined);
      setGroupToExpand(undefined);

      empty && handleCreateInstruction();
    }
    // deps: effect should only run when the component mounts with an open state
  }, [open]);

  return (
    <AiContextModalFrame open={open} onClose={onClose} title="Manage instructions" onAddMore={handleCreateInstruction}>
      <div className={styles.instructionsGrid}>
        <InstructionsPanel
          appearance="grid"
          active={open}
          groupToExpand={groupToExpand}
          instructionToFocus={instructionToFocus}
          documentInstructions={documentInstructions}
          groupedInstructions={groupedInstructions}
          onInstructionDelete={onInstructionDelete}
          onInstructionEnabledChange={onInstructionEnabledChange}
          onAddInstruction={handleCreateInstruction}
          editorTestId={editorTestId}
        />
      </div>
    </AiContextModalFrame>
  );
};

export interface ManageInstructionsModalProps {
  open: boolean;
  empty?: boolean;
  onClose: () => void;
  onInstructionCreate?: (instructionId: string) => void;
  editorTestId?: string;
  onInstructionDelete?: (instructionId: string) => void | Promise<void>;
  groupedInstructions?: GroupedInstruction[];
  documentInstructions?: Record<string, boolean>;
  onInstructionEnabledChange: (instructionId: string, enabled: boolean, blockId?: string) => Promise<any>;
}
